import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Chip, IconButton, Switch, Tooltip } from "@mui/material";
import React, { useState } from "react";

import MDTypography from "@/components/atoms/MDTypography";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { PRODUCT_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useProductData from "@/hooks/data/use-product-data";
import useUpdateProduct from "@/hooks/requests/product/use-update-product";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import { formatVariationsLabel } from "@/helpers/variation-helpers";
import IProduct from "@/interfaces/product.interface";
import { ISku } from "@/interfaces/sku.interface";
import { ProductsMessages } from "@/messages/products/product.messages";

import { ProductPopover } from "../ProductPopover";

function getSkuVariationLabel(sku: ISku, product: IProduct): string {
  return formatVariationsLabel(sku.variations, product.variations);
}

interface Props {
  product: IProduct;
  index: number;
  messages: ProductsMessages;
}

export function ProductTableRow({ product, index, messages }: Props) {
  const { update } = useUpdateProduct();
  const { mutate } = useMutate();
  const data = useProductData({ product, messages });
  const [isExpanded, setIsExpanded] = useState(false);

  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IProduct>();

  const hasNoPermissionToEdit = useHasNoPermission(PRODUCT_PERMISSIONS.EDIT);

  const handleToggleActive = async (product: IProduct) => {
    const _id = product.id;
    const formData = { ...product, active: !product.active };
    const successMessage = messages.activateProductSuccess;
    const errorMessage = messages.activateProductError;

    const onSuccess = () => mutate(SWR_KEYS.getProducts);

    await update({ _id, formData, successMessage, errorMessage, onSuccess });
  };

  return (
    <>
      <TableRow index={index}>
        <TableCell>
          <Tooltip title={isExpanded ? messages.collapseSku : messages.expandSku}>
            <span>
              {/* Tooltip requires the child to be a single element */}
              <IconButton
                disabled={data.skus && data.skus.length <= 0}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>

        <TableCell>{data.code}</TableCell>
        <TableCell>{data.title}</TableCell>
        <TableCell>{data.brand}</TableCell>
        <TableCell>{data.totalStock}</TableCell>
        <TableCell>{data.variationLabel}</TableCell>
        <TableCell>
          <Switch
            checked={data.isActive}
            disabled={hasNoPermissionToEdit}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(product);
            }}
          />
        </TableCell>
        <TableCell>
          <IconButton onClick={(e) => openPopover(e, product)}>
            <MoreHorizIcon />
          </IconButton>

          {selected && (
            <ProductPopover
              isOpen={isOpen}
              anchorEl={anchorEl}
              onClose={closePopover}
              messages={messages}
              selected={selected}
            />
          )}
        </TableCell>
      </TableRow>

      {isExpanded &&
        data.skus &&
        data.skus.length > 0 &&
        data.skus.map((sku, skuIndex) => (
          <TableRow
            key={sku.id}
            index={index + skuIndex + 1}
            sx={{ opacity: sku.active === false ? 0.6 : 1 }}
          >
            <TableCell colSpan={1}></TableCell>
            <TableCell>
              <MDTypography variant="body2" color="text">
                {sku.code}
              </MDTypography>
            </TableCell>
            <TableCell>
              <MDTypography variant="body2" color="text">
                {sku.title}
              </MDTypography>
            </TableCell>
            <TableCell>
              <MDTypography variant="body2" color="text">
                {data.brand}
              </MDTypography>
            </TableCell>
            <TableCell>
              <MDTypography variant="body2" color="text">
                {sku.stock ?? "-"}
              </MDTypography>
            </TableCell>
            <TableCell>
              <MDTypography variant="body2" color="text">
                {getSkuVariationLabel(sku, product)}
              </MDTypography>
            </TableCell>
            <TableCell>
              {sku.active === false && (
                <Chip label={messages.skuInactiveLabel} size="small" color="default" variant="outlined" />
              )}
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        ))}
    </>
  );
}
